// src/utils/reimbursementUtils.js
// Utility functions for Travel & Reimbursement documents handling

import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

/**
 * Safely extracts all attached PDF/image documents from a reimbursement claim.
 * Handles all known legacy and current field formats:
 * - claim.documents (array of objects or URLs)
 * - claim.attachments (array of objects or URLs)
 * - claim.fileLinks (array of objects or URLs)
 * - claim.receiptUrls (array of objects or URLs)
 * - claim.documentUrls (array of objects or URLs)
 * - claim.bills (array of objects or URLs)
 * - claim.billDocuments (array of objects or URLs)
 * - claim.pdfs (array of objects or URLs)
 * - claim.files (array of objects or URLs)
 * - Single string fields: claim.receiptUrl, claim.receipt, claim.billUrl, claim.documentUrl, claim.pdfUrl, claim.fileUrl
 * - Nested row files in claim.transport, claim.accom, claim.meals, claim.other
 *
 * @param {Object} claim - The reimbursement claim Firestore document data
 * @returns {Array<{ id: string, name: string, url: string, category: string, size: number|null }>}
 */
export function extractClaimDocuments(claim) {
  if (!claim || typeof claim !== "object") return [];

  const docs = [];
  const seenUrls = new Set();

  const addDoc = (item, defaultName = "Bill") => {
    if (!item) return;

    let name = "";
    let url = "";
    let category = "";
    let size = null;

    if (typeof item === "string") {
      url = item.trim();
      name = defaultName;
    } else if (typeof item === "object") {
      url = item.url || item.downloadURL || item.fileUrl || item.link || item.uri || "";
      if (!url && item.base64) {
        const mime = item.mimeType || "application/pdf";
        url = `data:${mime};base64,${item.base64}`;
      }
      name = item.name || item.fileName || item.filename || defaultName;
      category = item.category || "";
      size = typeof item.size === "number" ? item.size : null;
    }

    if (url && typeof url === "string" && url.trim().length > 0) {
      const cleanUrl = url.trim();
      if (!seenUrls.has(cleanUrl)) {
        seenUrls.add(cleanUrl);
        docs.push({
          id: item?.id || `doc_${docs.length + 1}_${Math.random().toString(36).slice(2, 7)}`,
          name: name || `Bill ${docs.length + 1}`,
          url: cleanUrl,
          category,
          size,
        });
      }
    }
  };

  // 1. Array fields on claim root and emp
  const arrayFieldCandidates = [
    claim.documents,
    claim.attachments,
    claim.fileLinks,
    claim.receiptUrls,
    claim.documentUrls,
    claim.bills,
    claim.billDocuments,
    claim.pdfs,
    claim.files,
    claim.emp?.documents,
    claim.emp?.attachments,
    claim.emp?.fileLinks,
    claim.emp?.files,
    claim.emp?.receiptUrls,
  ];

  arrayFieldCandidates.forEach((arr) => {
    if (Array.isArray(arr)) {
      arr.forEach((docItem, idx) => {
        addDoc(docItem, `Bill ${docs.length + 1}`);
      });
    }
  });

  // 2. Single string URL fields on claim root and emp
  const singleFieldCandidates = [
    { val: claim.receiptUrl, name: "Receipt" },
    { val: claim.receipt, name: "Receipt" },
    { val: claim.billUrl, name: "Bill" },
    { val: claim.documentUrl, name: "Document" },
    { val: claim.pdfUrl, name: "PDF Bill" },
    { val: claim.fileUrl, name: "Attached File" },
    { val: claim.emp?.receiptUrl, name: "Receipt" },
    { val: claim.emp?.fileUrl, name: "Attached File" },
  ];

  singleFieldCandidates.forEach(({ val, name }) => {
    if (typeof val === "string" && val.trim()) {
      addDoc(val, name);
    } else if (val && typeof val === "object") {
      addDoc(val, name);
    }
  });

  // 3. Nested row files in expense breakdown sections
  const expenseSections = [
    { rows: claim.transport || claim.emp?.transport, label: "Transportation" },
    { rows: claim.accom || claim.emp?.accom, label: "Accommodation" },
    { rows: claim.meals || claim.emp?.meals, label: "Meals" },
    { rows: claim.other || claim.emp?.other, label: "Other" },
  ];

  expenseSections.forEach(({ rows, label }) => {
    if (Array.isArray(rows)) {
      rows.forEach((r, rIdx) => {
        if (r && Array.isArray(r.files)) {
          r.files.forEach((f, fIdx) => {
            addDoc(f, `${label} Bill (Row ${rIdx + 1}.${fIdx + 1})`);
          });
        }
        if (r && r.fileUrl) {
          addDoc(r.fileUrl, `${label} Bill (Row ${rIdx + 1})`);
        }
        if (r && r.receiptUrl) {
          addDoc(r.receiptUrl, `${label} Receipt (Row ${rIdx + 1})`);
        }
        if (r && r.url) {
          addDoc(r.url, `${label} Bill (Row ${rIdx + 1})`);
        }
      });
    }
  });

  return docs;
}

/**
 * Safely opens any PDF or file URL in a new browser window.
 * If the URL is a Data URI (Base64 data), converts it to a Blob and creates
 * an object URL to prevent browser security errors when opening Data URIs.
 *
 * @param {string|Object} docItem - URL string or document object containing { url, name }
 */
export function openDocument(docItem) {
  if (!docItem) return;

  const url = typeof docItem === "string" ? docItem.trim() : (docItem.url || "").trim();
  if (!url) return;

  // If it's a data URI (e.g. data:application/pdf;base64,...), convert to Blob URL
  if (url.startsWith("data:")) {
    try {
      const parts = url.split(";base64,");
      if (parts.length === 2) {
        const contentType = parts[0].replace("data:", "") || "application/pdf";
        const byteCharacters = atob(parts[1]);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: contentType });
        const blobUrl = URL.createObjectURL(blob);
        const win = window.open(blobUrl, "_blank", "noopener,noreferrer");
        if (!win) {
          window.location.href = blobUrl;
        }
        return;
      }
    } catch (err) {
      console.warn("Failed to convert data URI to blob, falling back to direct open:", err);
    }
  }

  // Standard web URL (Firebase Storage or HTTPS)
  const win = window.open(url, "_blank", "noopener,noreferrer");
  if (!win) {
    // Popup might have been blocked, try anchor click
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}

/**
 * Converts a File object to a Base64 Data URL.
 *
 * @param {File|Blob} file
 * @returns {Promise<string>}
 */
export function fileToDataUrl(file) {
  return new Promise((resolve) => {
    if (!file || !(file instanceof Blob || file instanceof File)) {
      resolve("");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result || "");
    reader.onerror = () => resolve("");
    reader.readAsDataURL(file);
  });
}

/**
 * Uploads a list of files to Firebase Storage with automatic Data URL fallback.
 * Guarantees that every attached document receives a valid accessible URL.
 */
export async function uploadClaimDocuments(fileItems = [], claimId, storageInstance) {
  const uploaded = [];

  for (let i = 0; i < fileItems.length; i++) {
    const item = fileItems[i];
    const fileObj = item.file || item;

    if (!fileObj || !(fileObj instanceof Blob || fileObj instanceof File)) {
      if (item.url && typeof item.url === "string") {
        uploaded.push({
          id: item.id || `doc_${i + 1}`,
          name: item.name || `Bill ${i + 1}`,
          url: item.url,
          category: item.category || "General",
          size: item.size || 0,
        });
      }
      continue;
    }

    const fileName = item.name || fileObj.name || `Bill_${i + 1}.pdf`;
    const safeName = fileName.replace(/[^a-zA-Z0-9.-]/g, "_");
    let docUrl = "";

    // 1. Try Firebase Storage upload with 3.5s timeout race
    if (storageInstance && claimId) {
      try {
        const uploadAction = (async () => {
          const fileRef = ref(storageInstance, `reimbursements/${claimId}/${Date.now()}_${i + 1}_${safeName}`);
          await uploadBytes(fileRef, fileObj);
          return await getDownloadURL(fileRef);
        })();

        const timeoutAction = new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Storage upload timeout")), 3500)
        );

        docUrl = await Promise.race([uploadAction, timeoutAction]);
      } catch (storageErr) {
        console.warn("Firebase Storage upload fallback for file:", fileName, storageErr);
      }
    }

    // 2. If storage upload fails or storage unavailable, fallback to Data URL
    if (!docUrl) {
      try {
        const rawDataUrl = await fileToDataUrl(fileObj);
        // If Data URL is under 400KB, it is safe for direct Firestore embedding
        if (rawDataUrl && rawDataUrl.length < 400 * 1024) {
          docUrl = rawDataUrl;
        } else if (rawDataUrl) {
          // For larger files, keep Data URL for current session preview and store lightweight doc reference
          docUrl = rawDataUrl;
        }
      } catch (readerErr) {
        console.error("FileReader fallback failed:", readerErr);
      }
    }

    uploaded.push({
      id: item.id || `doc_${Date.now()}_${i + 1}`,
      name: fileName,
      url: docUrl || "",
      category: item.category || "General",
      size: fileObj.size || item.size || 0,
    });
  }

  return uploaded;
}
